import { useState, useRef } from 'react';
import { useForm, ValidationError } from '@formspree/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger);

export default function Contact() {
  const [state, handleSubmit] = useForm('mjgpdwel');
  const [emailError, setEmailError] = useState('');
  const container = useRef();

  useGSAP(() => {
    gsap.from('.contact-anim', {
      scrollTrigger: { trigger: container.current, start: 'top 75%' },
      y: 50, opacity: 0, duration: 1, stagger: 0.15, ease: 'power3.out',
    });
  }, { scope: container });

  const validateEmail = e => {
    const v = e.target.value;
    setEmailError(v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'Invalid email address.' : '');
  };

  return (
    <section id="contact" ref={container} style={{ padding: '8rem 0', borderTop: '1px solid var(--border)' }}>

      {/* Success overlay */}
      {state.succeeded && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 999,
          background: 'rgba(13,13,13,0.97)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '5rem', color: 'var(--gold)', marginBottom: '1rem' }}>✓</div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '2.5rem', marginBottom: '1rem' }}>Message Sent.</h3>
            <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>I'll get back to you within 24–48 hours.</p>
            <button onClick={() => window.location.reload()} className="btn btn-gold">Close</button>
          </div>
        </div>
      )}

      <div className="page-wrap">
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '5rem', flexWrap: 'wrap', gap: '2rem' }}>
          <div className="contact-anim">
            <span className="sec-num">04</span>
            <h2 className="display-lg" style={{ marginTop: '-1.5rem' }}>Contact</h2>
          </div>
          <p className="contact-anim" style={{ maxWidth: '42ch', color: 'var(--muted)', fontSize: '1.05rem', lineHeight: 1.8 }}>
            Looking for an intern, a collaborator, or someone to build your next AI-powered web app? Let's talk.
          </p>
        </div>

        <hr className="hr" style={{ marginBottom: '5rem' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '6rem' }} className="contact-grid">

          {/* Left info column */}
          <div>
            <div className="contact-anim">
              <div className="hr-gold" />
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginBottom: '2rem', color: 'var(--white)' }}>Direct Contact</h3>
            </div>

            {[
              { label: 'Email', value: 'pradeepkumarsirigirisetty@gmail.com', href: 'mailto:pradeepkumarsirigirisetty@gmail.com' },
              { label: 'Phone', value: '+91 78937 89097', href: 'tel:+917893789097' },
              { label: 'GitHub', value: 'Pradeepkumarsirigirisetty', href: 'https://github.com/Pradeepkumarsirigirisetty' },
              { label: 'LinkedIn', value: 'View Profile', href: 'https://www.linkedin.com/in/sirigirisetty-pradeep-kumar-218591340/' },
              { label: 'Resume', value: 'Download PDF', href: 'Pradeep_Kumar_Resume.pdf' },
            ].map(item => (
              <div key={item.label} className="contact-anim" style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '1.1rem 0', borderBottom: '1px solid var(--border)',
              }}>
                <span className="label">{item.label}</span>
                <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
                  className="inline-link" style={{ fontSize: '0.85rem', textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
                  {item.value}
                </a>
              </div>
            ))}
          </div>

          {/* Right form */}
          <div className="contact-anim">
            <div className="hr-gold" />
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginBottom: '2rem', color: 'var(--white)' }}>Send a Message</h3>
            <form onSubmit={e => { if (emailError) { e.preventDefault(); return; } handleSubmit(e); }}
              style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

              <div>
                <label className="form-label" htmlFor="name">Your Name</label>
                <input id="name" name="name" type="text" className="form-input" placeholder="Pradeep Kumar" required />
              </div>
              <div>
                <label className="form-label" htmlFor="email">Email Address</label>
                <input id="email" name="email" type="email" className="form-input" placeholder="you@email.com"
                  required onChange={validateEmail}
                  style={{ borderColor: emailError ? '#ef4444' : '' }} />
                {emailError && <span style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '0.35rem', display: 'block' }}>{emailError}</span>}
                <ValidationError field="email" prefix="Email" errors={state.errors} style={{ color: '#ef4444', fontSize: '0.75rem' }} />
              </div>
              <div>
                <label className="form-label" htmlFor="message">Message</label>
                <textarea id="message" name="message" rows={5} className="form-input"
                  placeholder="Hi Pradeep, I'd like to talk about…" required style={{ resize: 'vertical' }} />
                <ValidationError field="message" prefix="Message" errors={state.errors} style={{ color: '#ef4444', fontSize: '0.75rem' }} />
              </div>

              <button type="submit" className="btn btn-gold" disabled={state.submitting || !!emailError}
                style={{ width: '100%', justifyContent: 'center', opacity: state.submitting || emailError ? 0.6 : 1 }}>
                {state.submitting ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          </div>

        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .contact-grid { grid-template-columns: 1fr !important; gap: 4rem !important; }
        }
      `}</style>
    </section>
  );
}